// @ts-nocheck — Pre-existing third-party component type errors from JSX→TSX migration; typed props are enforced via ProposalDrawerSystemData.
import { makeStyles } from '@material-ui/core'
import BookOutlinedIcon from '@material-ui/icons/BookOutlined'
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined'
import GavelIcon from '@material-ui/icons/GavelOutlined'
import classNames from 'classnames'
import { LOCALES_SUPPORTED_CUSTOMER_FACING } from 'constants/locales'
import { ComponentVersions_2_1 } from 'constants/uxVersions'
import { authSelectors } from 'ducks/auth'
import { orgSelectors } from 'ducks/orgs'
import { permissionsSelectors } from 'ducks/permissions'
import { TEST_ID_PROPOSAL_TEMPLATE_FIELD } from 'e2e/playwrightTestIds'
import CustomField from 'elements/field/CustomField'
import { SharedEntityChipsInput } from 'elements/field/SharedEntityChipsInput'
import SharedEntitySelectInput from 'elements/field/SharedEntitySelectInput'
import DateInput from 'elements/input/DateInput'
import InfoTooltip from 'elements/tooltip/InfoTooltip'
import UnresolvableReferenceInput from 'elements/UnresolvableReferenceInput'
import EditorComponent from 'elements/wysiwyg/EditorComponentWrapper'
import useTrackComponent from 'hooks/eventTracking/useTrackComponent'
import type { ContractTemplateOverrides } from 'opensolar-common'
import {
  buildOverridesAfterChange,
  ContractTemplateModeEnum,
  ContractTemplateSettings,
  isPaymentOptionContractMode,
} from 'opensolar-common'
import { Button, ComponentVersionsInherit, Divider, styled, Typography } from 'opensolar-ui'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { ReferenceArrayInput, SaveButton, SelectInput, Toolbar, useTranslate } from 'react-admin'
import { useForm } from 'react-final-form'
import { useSelector } from 'react-redux'
import { getLanguageChoices } from 'resources/projects/choices'
import { ProposalTemplateEdit } from 'resources/proposalTemplates/ProposalTemplates'
import { ProposalTemplateTypeEnum } from 'resources/proposalTemplates/types'
import restClient from 'restClient'
import type { ProposalDrawerSystemData } from 'types/systems'
import { ActionType, EventType } from 'types/tracking'
import { useFeatureFlag } from 'util/featureFlags'
import { dateParser } from 'util/misc'
import useMemoizedSelector from 'util/useMemoizedSelector'
import DialogEditor from '../../elements/DialogEditor'
import { formSharingParams, formSharingParamsString } from '../info/orgSharing/util'
import ContractTemplateEditor from './ContractTemplateEditor'
import ProposalTemplateEditor from './ProposalTemplateEditor'
import PvsellProposalDialog from './PvsellProposalDialog'

const useStyles = makeStyles(
  (theme) => ({
    container: {
      padding: '10px 20px',
    },
    fieldsWrapper: {
      display: 'flex',
      alignItems: 'center',
      // paddingRight: 20,
      [theme.breakpoints.up('md')]: {
        '& > div': {
          minWidth: '130px',
        },
      },
      [theme.breakpoints.down('xs')]: {
        display: 'block',
        margin: '10px 5px',

        '& .OSUI-FormControl-root': {
          width: '100%',
        },
      },
    },
    input: {
      margin: '16px 5px',
      [theme.breakpoints.down('xs')]: {
        margin: 5,
      },

      width: 200,
      flexGrow: 1,
      '& .MuiFormLabel-root': {
        color: 'black',
      },
    },
    languageInput: {
      [theme.breakpoints.up('sm')]: {
        marginLeft: '5px !important',
      },
      marginBottom: '0 !important',
    },
    chipsInput: {
      margin: '16px 5px',
      [theme.breakpoints.down('xs')]: {
        margin: 5,
      },
      '& .MuiFormLabel-root': {
        color: 'black',
      },
    },

    button: {
      flexGrow: 0,
      paddingRight: 20,
      [theme.breakpoints.up('md')]: {
        maxWidth: 130,
        textAlign: 'center',
      },
    },
  }),
  { name: 'customise-proposal-inputs' }
)

const SubViewShell = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  flex: '1 1 auto',
  minHeight: 0,
  minWidth: 0,
  height: '100%',
  width: '100%',
  boxSizing: 'border-box',
  overflow: 'hidden',
})

const SubViewHeader = styled('div')({
  flex: '0 0 auto',
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '10px 20px',
  borderBottom: '1px solid #eee',
  background: '#fff',
  boxSizing: 'border-box',
})

const SubViewHeaderTitle = styled(Typography)({
  fontWeight: 'bold',
})

const SubViewBody = styled('div')({
  flex: '1 1 auto',
  minHeight: 0,
  minWidth: 0,
  overflowX: 'hidden',
  overflowY: 'auto',
  boxSizing: 'border-box',
  padding: '8px 16px',
  '& *': {
    minWidth: 0,
  },
  // Strip react-admin's internal paddings. Each rule pairs the dev JSS selector
  // (`RaEdit-*`) with a structural fallback (`.edit-page`, `.simple-form`,
  // `.MuiCardContent-root`) that survives prod JSS prefix mangling.
  '& [class*="RaEdit-main-"], & > .edit-page > div': {
    display: 'block',
    margin: 0,
  },
  '& [class*="RaEdit-root-"], & > .edit-page': {
    padding: 0,
  },
  '& [class*="RaEdit-card-"], & > .edit-page > div > div': {
    padding: 0,
    boxShadow: 'none',
    backgroundColor: 'transparent',
  },
  '& [class*="RaSimpleForm-form-"], & form.simple-form': {
    padding: 0,
  },
  '& [class*="RaCardContentInner-root"], & form.simple-form > .MuiCardContent-root': {
    padding: 0,
  },
})

const SIDEBAR_SAVE_TOOLBAR_STYLE: React.CSSProperties = {
  position: 'sticky',
  bottom: 0,
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  padding: '8px 16px',
  borderTop: '1px solid #eee',
  background: '#fff',
  boxSizing: 'border-box',
  minHeight: 56,
  zIndex: 2,
}

const getDefaultTemplate = (choices, org, isShared) => {
  let defaultTemplate = choices.find((choice) => choice.is_default && org === choice.org)
  if (isShared) {
    const defaultExternal = choices.find(
      (choice) => choice.is_default && org !== choice.org && choice.share_with_orgs.includes(org)
    )
    defaultTemplate = defaultExternal || defaultTemplate
  }
  return defaultTemplate
}

const getTemplateById = (url, choices) => {
  const template = choices && choices.find((choice) => choice.url === url)
  return template
}
const InputWithEditor = ({ component, editorComponent, buttonLabel, onEditHandlerOverride, ...props }) => {
  const { name, choices } = props
  const [isOpen, setOpen] = useState(false)
  const form = useForm()
  const translate = useTranslate()
  const formValue = form.getState().values
  const classes = useStyles()
  const sharedWith = formValue.shared_with
  const isShared = !!sharedWith?.length
  const defaultTemplate = getDefaultTemplate(choices, formValue.org, isShared)
  const currentSelectedTemplate = formValue[name] === null ? defaultTemplate : getTemplateById(formValue[name], choices)
  const id = currentSelectedTemplate && currentSelectedTemplate.id

  const org = useSelector(orgSelectors.getOrg)
  const [newButtonLabel, setButtonLabel] = useState(buttonLabel)
  const [emptyText, setEmptyText] = useState('')

  const { trackEvent } = useTrackComponent({
    componentKey: 'proposal_inputs',
    eventName: 'User Edited Proposal Template',
  })

  const handleEditButton = (e) => {
    e.preventDefault()
    trackEvent(
      EventType.USER_INTERACTION,
      { type: ActionType.CLICK },
      {
        currentSelectedTemplate,
        source: newButtonLabel,
      }
    )
    if (typeof onEditHandlerOverride === 'function') {
      // Defer to the parent to render the editor (e.g. Proposal sidebar sub-view) instead
      // of opening DialogEditor. Send the bits the parent needs to render the editor —
      // same data DialogEditor used to inject (id, templateOwner).
      onEditHandlerOverride({
        id,
        templateOwner: currentSelectedTemplate?.org === org?.url,
      })
    } else {
      setOpen(true)
    }
  }

  const getDefaultTemplateText = (defaultTemplate) => {
    const defaultEnd = defaultTemplate
      ? ` (${defaultTemplate.title})`
      : org?.id !== formValue.org_id
        ? ` (${translate('Unshared Item')})`
        : ''
    return translate('Default Template') + defaultEnd
  }

  useEffect(() => {
    if (isShared) {
      setButtonLabel((currentSelectedTemplate?.org === org?.url ? 'Edit ' : 'View ') + buttonLabel)
    } else {
      setButtonLabel('Edit ' + buttonLabel)
    }
    setEmptyText(getDefaultTemplateText(defaultTemplate))
  }, [currentSelectedTemplate, sharedWith, org])

  return (
    <div className={classes.fieldsWrapper}>
      {React.createElement(component, {
        allowEmpty: true,
        emptyValue: '',
        emptyText: <i style={{ color: '#aaa' }}>{emptyText}</i>,
        ...props,
      })}
      <div className={classes.button}>
        {id && (
          <>
            <ComponentVersionsInherit versions={ComponentVersions_2_1}>
              <Button variant="contained" size="small" style={{ textTransform: 'none' }} onClick={handleEditButton}>
                <span>{translate(newButtonLabel)}</span>
              </Button>
            </ComponentVersionsInherit>
            <DialogEditor
              isOpen={isOpen}
              handleClose={() => setOpen(false)}
              id={id}
              editorComponent={editorComponent}
              editorProps={{ templateOwner: currentSelectedTemplate?.org === org?.url }}
            />
          </>
        )}
      </div>
    </div>
  )
}

const useStyles2 = makeStyles((theme) => ({
  wysiwygHeading: {
    margin: 0,
    color: 'black',
    whiteSpace: 'normal',
    borderBottom: 'none',
  },
  wysiwygWrapper: { margin: '16px 0', display: 'flex', flexDirection: 'column' },
  infoIcon: {
    color: 'rgb(0, 148, 255)',
  },
  popoverContent: {
    maxWidth: 300,
    padding: 10,
    fontSize: 12,
    cursor: 'default',
    background: 'rgba(0,0,0,0.2)',
  },
  icon: {
    width: 24,
    height: 24,
    marginRight: 10,
    verticalAlign: 'middle',
  },
  heading: {
    verticalAlign: 'middle',
    whiteSpace: 'normal',
    cursor: 'pointer',
  },
  accordion: {
    margin: '16px 0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
}))

const EditorAccordion = ({ source, heading, handleChange, tooltip, icon }) => {
  const [expanded, setExpanded] = useState(false)

  const form = useForm()
  const values = form.getState().values
  useEffect(() => {
    if (values[source]) {
      setExpanded(true)
    }
  }, [])
  const classes = useStyles2()
  return expanded ? (
    <div className={classes.wysiwygWrapper}>
      <h3 className={classes.wysiwygHeading}>{heading}</h3>
      <EditorComponent fieldName={source} allowUploadImage={true} onChange={handleChange} mentionContext="empty" />
      <CustomField name={source} component="input" style={{ display: 'none' }} />
    </div>
  ) : (
    <div className={classes.accordion}>
      <div>
        {React.createElement(icon, { className: classes.icon })}
        <span onClick={() => setExpanded(true)} className={classes.heading}>
          {heading}
        </span>
      </div>
      <InfoTooltip title={tooltip} />
    </div>
  )
}

export const onChangeProposalTemplateSettings = (form, newProposalTemplateData) => {
  const isFieldExist = form.getFieldState('proposal_template_settings')
  if (!isFieldExist) {
    form.registerField('proposal_template_settings', () => {}, {})
  }
  form.change('proposal_template_settings', {
    pvprosell_template_id: newProposalTemplateData?.pvprosell_template_id,
    title: newProposalTemplateData?.title,
  })

  // Keep proposal_template registered after V2/non-solar unmounts ProposalDrawer (no visible Field).
  // Do not use getFieldState — SelectProposalTemplateInput also registers this name while mounted.
  if (!form['__proposalTemplateKeepAliveRegistered']) {
    form.registerField('proposal_template', () => {}, {})
    form['__proposalTemplateKeepAliveRegistered'] = true
  }
}

const SelectProposalTemplateInput = (props) => {
  const { choices, input } = props
  const form = useForm()
  const enhancedOnChange = (e) => {
    input.onChange(e)
    const newProposalTemplate = e.target.value
    const newProposalTemplateData = choices.find((choice) => choice.url === newProposalTemplate)
    onChangeProposalTemplateSettings(form, newProposalTemplateData)
  }
  return (
    <SharedEntitySelectInput
      {...props}
      onChange={enhancedOnChange}
      variant="outlined"
      data-testid={TEST_ID_PROPOSAL_TEMPLATE_FIELD}
    />
  )
}

export interface ProposalInputsProps {
  handleChange: () => void
  systems?: ProposalDrawerSystemData[]
  buttonLabel?: string
}

const ProposalInputs = ({ handleChange, systems, buttonLabel }: ProposalInputsProps) => {
  const translate = useTranslate()
  const enableClassicEditInSidebar = useFeatureFlag('enable_classic_proposal_sidebar_edit', 'on')
  const [showPvsellDialog, setPvsellDialogVisibility] = useState(false)
  const [showProposalTemplateForm, setShowProposalTemplateForm] = useState(false)
  const [editorPayload, setEditorPayload] = useState<null | { id: string | number; templateOwner: boolean }>(null)
  const classes = useStyles()
  const projectId = useSelector((state) => state.projectId)
  const proposalTemplateAccessRights = useSelector((state) =>
    permissionsSelectors.getPermissionByKey(state, 'proposal_template')
  )
  const api_key_pvsell = useSelector(authSelectors.getPvSellKey)
  const form = useForm()
  const formValue = form.getState().values
  const contractTempateMode = formValue?.contract_template_mode
  const pvSellTemplateId = formValue?.proposal_template_settings?.pvprosell_template_id
  const [contracts, setContracts] = useState([])
  const [showContractTempateMode, setShowContractTempateMode] = React.useState(() =>
    isPaymentOptionContractMode(contractTempateMode)
  )
  const [contractEditorOpen, setContractEditorOpen] = useState(false)
  const [contractEditorTemplate, setContractEditorTemplate] = useState(null)
  const [localContractOverridesBySystem, setLocalContractOverridesBySystem] = useState<
    Record<string, ContractTemplateOverrides>
  >({})

  const currOrgId = useSelector(authSelectors.getOrgId)
  const org = useSelector(orgSelectors.getOrg)
  const restClientInstance = restClient(window.API_ROOT + '/api')
  const contractPermissions = useMemoizedSelector((state) =>
    permissionsSelectors.getProjectPermissionByKey(state, 'contract_template')
  )

  useEffect(() => {
    if (contractPermissions?.allowView) {
      var contractsUrl = 'contracts/' + formSharingParamsString(currOrgId, form.getState().values, 'fieldset=list')
      restClientInstance('CUSTOM_GET', 'contracts', {
        url: contractsUrl,
      })
        .then((response) => {
          const contracts = response.data
          setContracts(contracts)
          if (isPaymentOptionContractMode(contractTempateMode)) {
            setShowContractTempateMode(true)
          }
        })
        .catch((error) => {
          console.log('Failed to load contracts: ', error)
        })
    }
  }, [contractPermissions?.allowView])

  const systemsOverridesKey = useMemo(
    () => JSON.stringify(systems?.map((s) => ({ uuid: s.uuid, overrides: s.contract_template_overrides }))),
    [systems]
  )

  useEffect(() => {
    setLocalContractOverridesBySystem({})
  }, [systemsOverridesKey])

  const handleContractModeClick = () => {
    form.mutators.markFieldAsDirty('contract_template_mode')
    form.mutators.updateField(
      'contract_template_mode',
      showContractTempateMode ? ContractTemplateModeEnum.project : ContractTemplateModeEnum.payment_option
    )

    if (contractTempateMode === ContractTemplateModeEnum.payment_option && systems?.length) {
      systems.forEach((systemData) => {
        let osSystem = window.editor.objectByUuid(systemData.uuid)
        let contract_template_overrides = osSystem.contract_template_overrides || {}
        setValueCmd(osSystem, 'contract_template_overrides', contract_template_overrides)
      })
    } else {
      handleChange()
    }

    setShowContractTempateMode(!showContractTempateMode)
  }

  function setValueCmd(osSystem, prop, value) {
    window.editor.execute(new window.SetValueCommand(osSystem, prop, value))

    // Ideally this could be handled automatically in response to the SetValueCommand but it appears that
    // this is not detected automatically when we edit contract template overrides in the left drawer
    // in Online Proposal section. So for no we will trigger manually. This is copied from
    // ProjectForm.handleHistoryChanged()
    form.mutators.markFieldAsDirty('design')
    form.change('design', 'has unsaved change')

    // Trigger proposal to reload with our new changes
    // @TODO: Add a delay so quickly changing multiple contracts for multiple payment options will only
    // result in only loading proposal data once.
    handleChange()
  }

  const onPaymentOptionContractChange = (systemUuid, paymentOptionId, contractId) => {
    const osSystem = window.editor?.objectByUuid(systemUuid)
    if (!osSystem) return

    const currentOverrides = localContractOverridesBySystem[systemUuid] ?? osSystem.contract_template_overrides
    const nextOverrides = buildOverridesAfterChange(currentOverrides, paymentOptionId, contractId)

    setLocalContractOverridesBySystem((prev) => ({
      ...prev,
      [systemUuid]: nextOverrides,
    }))
    setValueCmd(osSystem, 'contract_template_overrides', nextOverrides)
  }

  const onProjectContractChange = (contractUrl) => {
    form.mutators.markFieldAsDirty('contract')
    form.mutators.updateField('contract', contractUrl)
    handleChange()
  }

  const closeSubView = useCallback(() => {
    setShowProposalTemplateForm(false)
    setEditorPayload(null)
  }, [])

  const sidebarSaveOnlyToolbar = useMemo(() => {
    const SidebarSaveOnlyToolbar = (toolbarProps: any) => {
      const isCrossOrg =
        toolbarProps.record?.org && toolbarProps.record.org.indexOf('/orgs/' + toolbarProps.org_id + '/') === -1
      return (
        <Toolbar {...toolbarProps} style={SIDEBAR_SAVE_TOOLBAR_STYLE}>
          <SaveButton
            redirect={false}
            submitOnEnter={true}
            raised={true}
            disabled={toolbarProps.disabled || isCrossOrg}
            onSave={(values: any, redirect: any, form: any, defaultSaveFunc: any) => {
              defaultSaveFunc(values, redirect, {
                onSuccess: () => {
                  closeSubView()
                  handleChange()
                },
              })
            }}
          />
        </Toolbar>
      )
    }
    return SidebarSaveOnlyToolbar
  }, [closeSubView, handleChange])

  const isSharedProject = !!formValue?.shared_with?.length
  const systemsForContractSettings = useMemo(
    () =>
      (systems || []).map((systemData) => ({
        ...systemData,
        contract_template_overrides:
          localContractOverridesBySystem[systemData.uuid] ??
          window.editor?.objectByUuid(systemData.uuid)?.contract_template_overrides ??
          systemData.contract_template_overrides,
      })),
    [systems, localContractOverridesBySystem]
  )

  if (showProposalTemplateForm && editorPayload) {
    return (
      <SubViewShell>
        <SubViewHeader>
          <Button variant="contained" size="small" style={{ textTransform: 'none' }} onClick={closeSubView}>
            <span>← {translate('Back')}</span>
          </Button>
          <SubViewHeaderTitle variant="subtitle1">{translate('Edit Proposal Template')}</SubViewHeaderTitle>
        </SubViewHeader>
        <SubViewBody>
          <ProposalTemplateEdit
            id={editorPayload.id}
            basePath="/proposal_templates"
            resource="proposal_templates"
            accessRights={proposalTemplateAccessRights}
            customToolbar={sidebarSaveOnlyToolbar}
            disablePreview={true}
            hasDelete={false}
            hideBreadCrumbs={true}
            component="div"
            hideTitle={true}
          />
        </SubViewBody>
      </SubViewShell>
    )
  }

  const onEditContract = (contract) => {
    setContractEditorTemplate(contract)
    setContractEditorOpen(true)
  }

  return (
    <div className={classes.container}>
      <UnresolvableReferenceInput
        source="proposal_templates"
        label={translate('Proposal Template')}
        filter={formSharingParams(currOrgId, formValue, {
          exclude_types: ProposalTemplateTypeEnum.proposalV2_grapes_swipe,
        })}
        onChange={handleChange}
        name="proposal_template"
        reference="proposal_templates"
        optionValueField="url"
        perPage={500}
        allowEmpty
        ignoreWarnings={['associatedReference']}
      >
        <InputWithEditor
          optionText="title"
          optionValue="url"
          component={SelectProposalTemplateInput}
          style={{ marginBottom: '10px', marginRight: '20px' }}
          options={{ className: classes.input, SelectProps: { displayEmpty: true } }}
          InputLabelProps={{
            shrink: true,
          }}
          buttonLabel={'Proposal Template'}
          editorComponent={<ProposalTemplateEditor handleChange={handleChange} projectId={projectId} />}
          onEditHandlerOverride={
            enableClassicEditInSidebar
              ? (payload) => {
                  setEditorPayload(payload)
                  setShowProposalTemplateForm(true)
                }
              : undefined
          }
        />
      </UnresolvableReferenceInput>

      {contractPermissions?.allowView !== false && (
        <div style={{ margin: '16px 5px' }}>
          <ContractTemplateSettings
            translate={translate}
            contracts={contracts}
            isPaymentOptionMode={showContractTempateMode}
            onModeToggle={handleContractModeClick}
            projectContractUrl={formValue?.contract}
            onProjectContractChange={onProjectContractChange}
            systems={systemsForContractSettings}
            onPaymentOptionContractChange={onPaymentOptionContractChange}
            currentOrgId={currOrgId}
            currentOrgUrl={org?.url}
            projectOrgId={formValue?.org_id}
            isSharedProject={isSharedProject}
            onEditContract={onEditContract}
            sharedWith={formValue?.shared_with}
            currentOrg={org}
            selectMenuZIndex={2500}
          />
        </div>
      )}
      {contractEditorTemplate && (
        <DialogEditor
          isOpen={contractEditorOpen}
          handleClose={() => setContractEditorOpen(false)}
          id={contractEditorTemplate.id}
          editorComponent={<ContractTemplateEditor handleChange={handleChange} />}
          editorProps={{ templateOwner: contractEditorTemplate?.org === org?.url }}
        />
      )}

      <div className={classes.fieldsWrapper}>
        <DateInput
          name="valid_until_date"
          className={classes.input}
          source="valid_until_date"
          id="salesValidUntilDate"
          parse={dateParser}
          onChange={handleChange}
          hideClearButton={true}
          label={translate('Valid Until')}
          variant="outlined"
        />
        {!pvSellTemplateId && (
          <SelectInput
            onChange={handleChange}
            className={classNames(classes.input, classes.languageInput)}
            InputLabelProps={{
              shrink: true,
            }}
            options={{
              SelectProps: {
                displayEmpty: true,
                renderValue: (locale) => (
                  <span>{LOCALES_SUPPORTED_CUSTOMER_FACING[locale] || translate('Default')}</span>
                ),
              },
            }}
            translateChoice={false}
            source="language_override"
            name="language_override"
            label={translate('Language')}
            choices={getLanguageChoices()}
            variant="outlined"
          />
        )}
      </div>
      {pvSellTemplateId && api_key_pvsell && (
        <div style={{ margin: '16px 5px', fontWeight: 'bolder' }}>
          <span>PVProsell PDF Proposals</span>
          <Button
            // style={{
            //   boxSizing: 'border-box',
            //   boxShadow: '1px 1px 2px rgba(0, 0, 0, 0.25)',
            //   borderRadius: '3px',
            // }}
            // labelWrapperStyle={{ color: '#4d4d4d', padding: '0 16px 0 16px' }}
            variant="contained"
            size="small"
            style={{ marginLeft: 10 }}
            className={classes.button}
            onClick={(e) => {
              setPvsellDialogVisibility(true)
            }}
          >
            {translate('Generate...')}
          </Button>

          <PvsellProposalDialog
            api_key_pvsell={api_key_pvsell}
            project_id={projectId}
            pvsell_selected_proposal_id={pvSellTemplateId}
            isOpen={showPvsellDialog}
            setOpen={setPvsellDialogVisibility}
          />
        </div>
      )}
      {!pvSellTemplateId && (
        <div style={{ paddingRight: 10 }}>
          <ReferenceArrayInput
            name="testimonials"
            source="testimonials"
            onChange={handleChange}
            filter={formSharingParams(currOrgId, formValue, {})}
            reference="testimonials"
            optionValueField="url"
          >
            <SharedEntityChipsInput
              options={{
                placeholder: translate('Choose case studies to override defaults'),
                className: classes.chipsInput,
              }}
              label={translate('Case Studies')}
              fullWidth={true}
              optionText="name"
              optionValue="url"
            />
          </ReferenceArrayInput>
        </div>
      )}
      <Divider style={{ margin: '20px 0' }} />
      <EditorAccordion
        source="proposal_message"
        handleChange={handleChange}
        icon={BookOutlinedIcon}
        heading={`${translate('Cover Message')} (${translate('override')})`}
        tooltip={
          <div>
            {translate(
              'Type your message into the box below to customize the message that your customer will see when they first access this proposal . You can include images or links, although this is not required.'
            )}
          </div>
        }
      />
      <EditorAccordion
        source="proposal_content"
        handleChange={handleChange}
        icon={DescriptionOutlinedIcon}
        heading={translate('Project-specific Proposal Content')}
        tooltip={
          <div>
            {translate(
              "Type your message into the box below to customize content that you'd like to appear in the proposal that is specific to this project. You can include images or links, although this is not required. To specify where this content will appear in your proposal, check out "
            )}
            <a
              href="https://support.opensolar.com/hc/en-us/articles/900004317783-Adding-Customised-Messages-Terms-for-a-Specific-Project"
              target="_blank"
              rel="noreferrer"
            >
              {translate('this HelpCenter article')}
            </a>
            .
          </div>
        }
      />
      <EditorAccordion
        source="contract_terms"
        handleChange={handleChange}
        icon={GavelIcon}
        heading={translate('Project-specific Contract Terms')}
        tooltip={
          <div>
            {translate(
              'Type your message into the box below to customize contract terms that are specific to this project. To specify where these terms will appear in your contract, check out '
            )}
            <a
              href="https://support.opensolar.com/hc/en-us/articles/900004317783-Adding-Customised-Messages-Terms-for-a-Specific-Project"
              target="_blank"
              rel="noreferrer"
            >
              {translate('this HelpCenter article')}
            </a>
            .
          </div>
        }
      />
    </div>
  )
}

export default ProposalInputs
